import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@finn/design-system/atoms/table';
import { ModalKey, useOpenModal } from '@finn/ua-modals';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { cn, useCurrentLocale } from '@finn/ui-utils';
import { useCallback, useContext } from 'react';

import { GenericVehicleDetails } from '../../../core';
import { useConfigureStore } from '../../../pdp-management';
import { VehicleContext } from '../../../product-cards';
import { DesktopGroupedProduct } from './DesktopGroupedProduct';
import { MobileGroupedProduct } from './MobileGroupedProduct';
import { useGroupedProducts } from './useGroupedProducts';

type Props = {
  openPCP: () => unknown;
  closedUserGroupId?: string;
};
export const GroupComparison = ({ openPCP, closedUserGroupId }: Props) => {
  const { vehicle, groupedProducts: initialGroupedProducts } =
    useContext(VehicleContext);
  const { setVehicle: setPCPVehicle } = useConfigureStore();
  const { setInfoDrawerVehicle } = useContext(VehicleContext);
  const { locale } = useCurrentLocale();
  const openModal = useOpenModal();
  const { products, updateColorOption, isLoadingProduct } = useGroupedProducts({
    initialProduct: vehicle,
    initialGroupedProducts,
  });

  const handleOpenPCP = useCallback(
    (selectedVehicle: GenericVehicleDetails, idx: number) => {
      setPCPVehicle(selectedVehicle, locale, false);
      openPCP();

      interactionTrackingEvent(TrackingEventName.CTA_CLICKED, {
        location: 'table',
        productId: selectedVehicle.id,
        productInfo: {
          position: idx,
        },
      });
    },
    [locale, openPCP, setPCPVehicle]
  );

  const handleMoreInformationClick = useCallback(
    (selectedVehicle: GenericVehicleDetails, idx: number) => {
      interactionTrackingEvent(TrackingEventName.MODAL_OPENED, {
        name: 'carinfo',
        configId: selectedVehicle.config_id,
        productInfo: {
          position: idx,
        },
      });
      setPCPVehicle(selectedVehicle, locale, false);
      setInfoDrawerVehicle(selectedVehicle);
      openModal(ModalKey.PDP_CAR_INFO);
    },
    [locale, openModal, setInfoDrawerVehicle]
  );

  const handleOnColorChange = useCallback(
    async (productIdx: number, newProductId: string) => {
      await updateColorOption(productIdx, newProductId);
    },
    [locale, updateColorOption]
  );

  return (
    <div className="container pt-20">
      <h2 id="group-comparison" className="mb-6 pt-4 web-t4-semibold">
        Verfügbare Konfigurationen
      </h2>
      <Table className="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/4 px-4">Ausstattungslinie</TableHead>
            <TableHead className="w-24 px-2">Farbe</TableHead>
            <TableHead className="w-[140px] px-2">Verfügbarkeit</TableHead>
            <TableHead className="w-20 px-2">Kraftstoff</TableHead>
            <TableHead className="w-20 px-2">Leistung</TableHead>
            <TableHead className="w-[100px] px-2">Antriebsart</TableHead>
            <TableHead className="w-[120px] px-2">Preis pro Monat</TableHead>
            <TableHead className="w-[60px] px-2" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product, idx) => (
            <DesktopGroupedProduct
              key={product.id}
              product={product}
              closedUserGroupId={closedUserGroupId}
              handleOpenPCP={() => handleOpenPCP(product, idx)}
              handleOnColorChange={(id) => handleOnColorChange(idx, id)}
              handleMoreInformationClick={() =>
                handleMoreInformationClick(product, idx)
              }
              isLoading={isLoadingProduct[idx]}
            />
          ))}
        </TableBody>
      </Table>

      <div
        className={cn(
          'grid rounded border-l border-t border-pearl md:hidden',
          products.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
        )}
      >
        {products.map((product, idx) => (
          <MobileGroupedProduct
            key={product.id}
            product={product}
            closedUserGroupId={closedUserGroupId}
            handleOpenPCP={() => handleOpenPCP(product, idx)}
            handleOnColorChange={(id) => handleOnColorChange(idx, id)}
            handleMoreInformationClick={() =>
              handleMoreInformationClick(product, idx)
            }
            isLoading={isLoadingProduct[idx]}
          />
        ))}
      </div>
    </div>
  );
};
